import type { Project, User } from '@/models'
import { type ComputedRef, computed, reactive, watch } from 'vue'
import { loadUserFromStorage, saveUserToStorage } from './userStorage'
import {
  createUser,
  getUserById,
  loginWithMemorablePassword,
  updateUser,
} from '@/services/userService'
//import { useI18n } from 'vue-i18n'

interface State {
  isRegistered: boolean
  darkMode: boolean
  user?: User
  currentProject: Project | undefined
  userId?: string
}

const state: State = reactive({
  isRegistered: false,
  darkMode: localStorage.getItem('darkMode') === 'true' ? true : false,
  user: undefined,
  currentProject: undefined,
  userId: localStorage.getItem('userId') || undefined,
})

interface Actions {
  toggleDarkMode: () => void
  createProject: (project: Project) => void
  deleteProject: (id: string) => void
  setCurrentProject: (project: Project | undefined) => void
  toggleResource: (type: 'case' | 'like' | 'build', itemID: string) => boolean
  updateWhatFreeText: (text: string) => void
  loadUser: () => Promise<void>
  createUserAccount: () => Promise<string>
  loginUser: (memorablePassword: string) => Promise<void>
  logout: () => void
  syncToBackend: () => Promise<void>
}

interface Getters {
  darkMode: ComputedRef<boolean>
  projects: ComputedRef<Project[]>
  currentProject: ComputedRef<Project | undefined>
  user: ComputedRef<User | undefined>
}

interface StoreInterface {
  actions: Actions
  getters: Getters
}

export function useAppStore(): StoreInterface {
  const getters = {
    get darkMode(): ComputedRef<boolean> {
      return computed(() => state.darkMode)
    },
    get projects(): ComputedRef<Project[]> {
      return computed(() => state.user?.projects ?? [])
    },
    get currentProject(): ComputedRef<Project | undefined> {
      return computed(() => state.currentProject)
    },
    get user(): ComputedRef<User | undefined> {
      return computed(() => state.user)
    },
  }

  const actions = {
    toggleDarkMode: function (): void {
      state.darkMode = !state.darkMode
      localStorage.setItem('darkMode', state.darkMode ? 'true' : 'false')
    },
    createProject(project: Project) {
      if (state.user) {
        state.user.projects.push(project)
        saveUserToStorage(state.user)
      }
    },
    deleteProject(id: string) {
      const projectIndex = state.user?.projects.findIndex((p) => p.id === id) ?? -1
      if (state.user && projectIndex > -1) state.user.projects.splice(projectIndex, 1)
    },
    setCurrentProject(project?: Project) {
      state.currentProject = project
    },
    updateWhatFreeText(text: string): void {
      if (state.currentProject) state.currentProject.whatFreeText = text
    },
    toggleResource(type: 'case' | 'like' | 'build', itemID: string): boolean {
      let toggleState = false
      if (state.user) {
        let list: string[] = []
        if (type === 'case') list = state.user.likedCases
        else if (type === 'like') list = state.user.likedResources
        else if (type === 'build' && state.currentProject) list = state.currentProject.resources

        const existingIndex = list.findIndex((id) => id === itemID)
        if (existingIndex > -1) list.splice(existingIndex, 1)
        else {
          list.push(itemID)
          toggleState = true
        }
        if (state.currentProject) state.currentProject.lastEdited = new Date()
      }
      return toggleState
    },
    async loadUser() {
      // Try to load from backend if we have a userId
      const userId = localStorage.getItem('userId')
      if (userId) {
        try {
          const userData = await getUserById(userId)
          state.user = {
            id: userData.id,
            projects: userData.projects,
            likedResources: userData.likedResources,
            likedCases: userData.likedCases,
          }
          // Also save to localStorage as backup
          saveUserToStorage(state.user)
          return
        } catch (error) {
          console.error('Failed to load user from backend:', error)
          // Fall through to localStorage
        }
      }

      // Fallback to localStorage
      const user = loadUserFromStorage()
      if (user && userId) {
        state.userId = userId
        state.user = user
      }
    },
    async createUserAccount(): Promise<string> {
      const response = await createUser()
      state.userId = response.user.id
      state.user = {
        id: response.user.id,
        projects: response.user.projects,
        likedResources: response.user.likedResources,
        likedCases: response.user.likedCases,
      }
      // Save userId to localStorage
      localStorage.setItem('userId', response.user.id)
      saveUserToStorage(state.user)
      // Return memorable password to display to user
      return response.memorablePassword
    },
    async loginUser(memorablePassword: string): Promise<void> {
      const response = await loginWithMemorablePassword(memorablePassword)
      state.userId = response.userId
      localStorage.setItem('userId', response.userId)
      // Load user data
      await actions.loadUser()
    },
    logout(): void {
      // Setting this to empty will prevent auto-login next time we visit the site
      localStorage.setItem('userId', '')
      state.userId = undefined
      state.user = undefined
      state.currentProject = undefined
    },
    async syncToBackend(): Promise<void> {
      if (!state.userId || !state.user) return

      try {
        await updateUser(state.userId, {
          projects: state.user.projects,
          likedResources: state.user.likedResources,
          likedCases: state.user.likedCases,
        })
      } catch (error) {
        console.error('Failed to sync to backend:', error)
        // Still save to localStorage as fallback
        saveUserToStorage(state.user)
      }
    },
  }

  // Watch for changes to projects and persist them automatically
  let saveTimeout: number | null = null
  watch(
    () => state.user,
    (updatedUser) => {
      // Debounce to avoid excessive writes during batch operations
      if (saveTimeout) clearTimeout(saveTimeout)
      saveTimeout = window.setTimeout(() => {
        if (updatedUser) {
          // Sync to backend if we have a userId, otherwise just localStorage
          if (state.userId) {
            actions.syncToBackend()
          } else {
            saveUserToStorage(updatedUser)
          }
        }
        saveTimeout = null
      }, 300)
    },
    { deep: true }, // Watch nested resource changes
  )

  return { actions, getters }
}

export default useAppStore
